import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { sendTransactions } from "@multiversx/sdk-dapp/services/transactions/sendTransactions";
import { refreshAccount } from "@multiversx/sdk-dapp/utils/account/refreshAccount";
import { useGetAccountInfo } from "@/hooks/sdkDappHooks";
import BigNumber from "bignumber.js";
import { A1X_MVX_DECIMALS } from "@/config/sharedConfig";
import { useRefreshAndSend } from "./useRefreshAndSend";

interface MultiversXTransactionParams {
  toAddress: string;
  amount: string;
  tokenIdentifier: string;
}

export const convertToHex = (amount: string) => {
  const amountBN = new BigNumber(amount);
  let hex = amountBN.toString(16);

  if (hex.length % 2 !== 0) {
    hex = "0" + hex;
  }

  return hex;
};

export const useMultiversXTransaction = () => {
  const { address } = useGetAccountInfo();
  const refreshAndSend = useRefreshAndSend();

  const sendTransaction = useCallback(
    async ({ toAddress, amount, tokenIdentifier }: MultiversXTransactionParams) => {
      if (!address) {
        throw new Error("MultiversX wallet not connected");
      }

      await refreshAccount();

      // Convert amount to proper decimals using BigNumber for precision
      const amountBN = new BigNumber(amount);
      const amountInSmallestUnit = amountBN.multipliedBy(new BigNumber(10).pow(A1X_MVX_DECIMALS));

      const amountInSmallestUnitHex = convertToHex(amountInSmallestUnit.toString());

      // Create ESDT transfer transaction
      const transaction = {
        value: "0",
        receiver: toAddress,
        data: `ESDTTransfer@${Buffer.from(tokenIdentifier).toString("hex")}@${amountInSmallestUnitHex}`,
        gasLimit: 500_000,
      };

      const result = await refreshAndSend([transaction], {
        processingMessage: "Processing bridge transaction...",
        errorMessage: "Bridge transaction failed",
        successMessage: "Bridge transaction sent successfully",
      });

      return result;
    },
    [address]
  );

  const mutation = useMutation({
    mutationFn: sendTransaction,
    onError: (error) => {
      console.error("MultiversX transaction error:", error);
    },
  });

  return {
    sendTransaction: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
